<template>
  <div id="broker-check" class="broker-check-page">
    <!-- Header -->
    <div class="vx-row mb-4">
      <div class="vx-col w-full flex items-center justify-between">
        <h2 class="page-title">Broker Check</h2>
        <div class="flex items-center">
          <vs-button type="border" icon-pack="feather" icon="icon-lock" class="mr-2" :to="{ name: 'broker-status' }">
            Broker Status
          </vs-button>
          <vs-button icon-pack="feather" icon="icon-credit-card" :to="{ name: 'broker-commission-payment' }">
            Paiement
          </vs-button>
        </div>
      </div>
    </div>

    <!-- Summary table: broker, volume in progress -->
    <div class="vx-row mb-6">
      <div class="vx-col w-full">
        <vx-card title="Summary by broker – volume & value" class="summary-card">
          <div slot="no-body" class="overflow-x-auto">
            <vs-table v-if="summaryTableWithTotal.length > 0" :data="summaryTableWithTotal" class="table-dark-inverted summary-table">
              <template slot="thead">
                <vs-th>Broker</vs-th>
                <vs-th>Volume in progress (m³)</vs-th>
                <vs-th>Value ex VAT (EUR)</vs-th>
              </template>
              <template slot-scope="{ data }">
                <vs-tr v-for="(row, idx) in data" :key="idx" :class="{ 'summary-total-row': row.isTotal }">
                  <vs-td>{{ row.broker }}</vs-td>
                  <vs-td>{{ Math.abs(parseFloat(row.volume || 0)).toFixed(2) }}</vs-td>
                  <vs-td>{{ formatSummaryValue(row) }}</vs-td>
                </vs-tr>
              </template>
            </vs-table>
            <p v-else class="p-4 text-gray-500">No data or loading...</p>
          </div>
        </vx-card>
      </div>
    </div>

    <!-- Approved quotations list -->
    <div class="vx-row">
      <div class="vx-col w-full">
        <vx-card title="Approved quotations (detail)" class="detail-card">
          <div v-if="loading" slot="no-body" class="loading-wrap">
            <div class="fill-row-loading">
              <div id="loading-broker-check" class="loading-example"></div>
            </div>
            <p>Loading...</p>
          </div>
          <div v-else-if="quotation_dashboard.length === 0" slot="no-body" class="empty-wrap">
            No approved quotation.
          </div>
          <div v-else slot="no-body" class="detail-body">
            <div
              v-for="(q, qIdx) in quotation_dashboard_sorted"
              :key="q.quotationref"
              class="quotation-block"
            >
              <!-- Header row: order status (success/danger) + broker + transport -->
              <div class="quotation-header-wrap">
                <vs-chip
                  :class="['quotation-chip', 'ag-grid-cell-chips']"
                  :color="isQuotationFullyReady(q) ? 'success' : 'danger'"
                >
                  <span class="quotation-header">
                    <img height="20px" v-if="q.country_code == 'BE'" src="@/assets/images/flags/BE.png" alt="BE" />
                    <img height="20px" v-else-if="q.country_code == 'HU'" src="@/assets/images/flags/HU.png" alt="HU" />
                    <img height="20px" v-else-if="q.country_code == 'AT'" src="@/assets/images/flags/AT.png" alt="AT" />
                    <img height="20px" v-else-if="q.country_code == 'NL'" src="@/assets/images/flags/NL.png" alt="NL" />
                    <img height="20px" v-else-if="q.country_code == 'CH'" src="@/assets/images/flags/CH.png" alt="CH" />
                    <img height="20px" v-else-if="q.country_code == 'DE'" src="@/assets/images/flags/DE.png" alt="DE" />
                    <img height="20px" v-else-if="q.country_code == 'ES'" src="@/assets/images/flags/ES.png" alt="ES" />
                    <img height="20px" v-else-if="q.country_code == 'FR'" src="@/assets/images/flags/FR.png" alt="FR" />
                    <img height="20px" v-else-if="q.country_code == 'IT'" src="@/assets/images/flags/IT.png" alt="IT" />
                    <img height="20px" v-else-if="q.country_code == 'LU'" src="@/assets/images/flags/LU.png" alt="LU" />
                    <img height="20px" v-else-if="q.country_code == 'RO'" src="@/assets/images/flags/RO.png" alt="RO" />
                    <img height="20px" v-else-if="q.country_code == 'GB'" src="@/assets/images/flags/UK.png" alt="GB" />
                    <img height="20px" v-else-if="q.country_code == 'PT'" src="@/assets/images/flags/PT.png" alt="PT" />
                    <img height="20px" width="20px" v-else-if="q.country_code == 'JP'" src="@/assets/images/flags/JP.png" style="border-radius: 100%;" alt="JP" />
                    <img height="20px" width="20px" v-else-if="q.country_code == 'CA'" src="@/assets/images/flags/CA.png" style="border-radius: 100%;" alt="CA" />
                    <img height="20px" v-else src="@/assets/images/flags/UE.png" alt="EU" />
                    <span class="quotation-flag-sep">&nbsp;&nbsp;</span>
                    <router-link :to="{ path: '/quotation/' + q.quotationref }" class="quotation-ref-link">#{{ q.quotationref }}</router-link>
                    <span class="quotation-sep">⟶</span>
                    <span class="quotation-date">{{ displayDate(q.date_delivery_desired) }}</span>
                    <span class="quotation-sep">⟶</span>
                    <span class="quotation-client">{{ q.client || '—' }}</span>
                    <span class="quotation-sep">⟶</span>
                    <span class="quotation-vol">{{ formatVolumeReady(q) }}</span>
                  </span>
                </vs-chip>
                <vs-chip
                  :class="['ag-grid-cell-chips', 'broker-chip']"
                  color="warning"
                >
                  <feather-icon icon="UserIcon" svgClasses="h-4 w-4 broker-chip-icon" />
                  <span class="broker-chip-label">{{ q.broker || '—' }}</span>
                  <span v-if="q.date_approved" class="broker-chip-approved"> · Approved at {{ displayDate(q.date_approved) }}</span>
                  <span v-if="q.created" class="broker-chip-created"> · Created at {{ displayDate(q.created) }}</span>
                </vs-chip>
                <vs-chip
                  v-if="q.transport"
                  :class="['ag-grid-cell-chips', 'transport-chip']"
                  :color="q.transport.people && q.transport.people.length > 0 ? '#A9CAB7' : '#c2d1f0'"
                >
                  <span>#{{ q.transport.id }} | {{ displayDate(q.transport.date) }}</span>
                  <feather-icon icon="TruckIcon" svgClasses="h-5 w-5" class="ml-1" />
                </vs-chip>
              </div>

              <!-- Delivery address -->
              <div class="addresses-row">
                <div class="address-block">
                  <span class="address-label">Delivery address</span>
                  <span class="address-value">{{ formatDeliveryAddress(q) }}</span>
                </div>
              </div>

              <!-- Internal note (mail_top) -->
              <vs-alert v-if="q.mail_top" class="internal-note-alert mb-3" color="warning" title="Internal note" :active="true">
                <div v-html="'' + q.mail_top"></div>
              </vs-alert>

              <!-- Product table (quotation view) + FSC, Sumal, Total -->
              <div v-if="getOperationDetails(q.quotationref) === undefined" class="products-loading py-4 text-center text-gray-500">
                Loading products...
              </div>
              <div v-else-if="getOperationDetails(q.quotationref) === null" class="products-loading py-4 text-center text-danger">
                Error loading products
              </div>
              <template v-else>
                <!-- FSC, Sumal (if Auto), Sumal (Mode) -->
                <div class="product-extra-info mb-3">
                  <table class="extra-info-table">
                    <tr>
                      <td class="font-semibold">FSC Required?</td>
                      <td>
                        <vs-chip v-if="getOperationDetails(q.quotationref).fsc" color="success" size="small">Yes</vs-chip>
                        <vs-chip v-else size="small">No</vs-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-semibold">Sumal (if Auto)</td>
                      <td>
                        <vs-chip v-if="needSumalForOperation(getOperationDetails(q.quotationref)) === 'Yes'" color="red" size="small">Yes</vs-chip>
                        <vs-chip v-else size="small">{{ needSumalForOperation(getOperationDetails(q.quotationref)) }}</vs-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-semibold">Sumal (Mode)</td>
                      <td>
                        <vs-chip v-if="getOperationDetails(q.quotationref).sumal_mode === 'forcetrue'" color="red" size="small">FORCED MANDATORY</vs-chip>
                        <vs-chip v-else-if="getOperationDetails(q.quotationref).sumal_mode === 'forcefalse'" color="success" size="small">FORCED NOT MANDATORY</vs-chip>
                        <vs-chip v-else-if="getOperationDetails(q.quotationref).sumal_mode === 'auto'" color="primary" size="small">Auto</vs-chip>
                        <span v-else>—</span>
                      </td>
                    </tr>
                  </table>
                </div>
                <vs-table :data="sortedProductsForOperation(getOperationDetails(q.quotationref))" class="products-table table-dark-inverted">
                  <template slot="thead">
                    <vs-th>Product</vs-th>
                    <vs-th>Quantity</vs-th>
                    <vs-th>Unit Price (€/UoM)</vs-th>
                    <vs-th>Unit of Measure</vs-th>
                    <vs-th>Volume Unit (m3/UoM)</vs-th>
                    <vs-th>Volume Total (m3)</vs-th>
                    <vs-th>(€/m3)</vs-th>
                    <vs-th>(€/ml)</vs-th>
                    <vs-th>(€/m²)</vs-th>
                    <vs-th>Total Price ex VAT (€)</vs-th>
                    <vs-th v-if="hasTva(getOperationDetails(q.quotationref))">VAT (%)</vs-th>
                    <vs-th v-if="hasTva(getOperationDetails(q.quotationref))">Total Price inc VAT (€)</vs-th>
                    <vs-th>Priority?</vs-th>
                  </template>
                  <template slot-scope="{ data }">
                    <vs-tr v-for="(tr, trIdx) in data" :key="trIdx">
                      <vs-td>{{ (tr.operations_products && tr.operations_products.description) || '—' }}</vs-td>
                      <vs-td>{{ tr.operations_products ? Math.abs(tr.operations_products.quantity).toFixed(4) : '—' }}</vs-td>
                      <vs-td>{{ tr.operations_products ? parseFloat(tr.operations_products.unit_price).toFixed(4) : '—' }}</vs-td>
                      <vs-td>{{ tr.uomsreference && tr.uomsreference.name_en ? tr.uomsreference.name_en : '—' }}</vs-td>
                      <vs-td>{{ tr.volume ? Math.abs(tr.volume).toFixed(4) : '—' }}</vs-td>
                      <vs-td>{{ tr.volume && tr.operations_products ? Math.abs(tr.volume * tr.operations_products.quantity).toFixed(4) : '—' }}</vs-td>
                      <vs-td>{{ tr.volume && tr.operations_products && (tr.volume * tr.operations_products.quantity) ? Math.abs((tr.operations_products.quantity * tr.operations_products.unit_price) / (tr.volume * tr.operations_products.quantity)).toFixed(4) : '—' }}</vs-td>
                      <vs-td>
                        <span v-if="tr.rollerlength">{{ tr.operations_products ? (tr.operations_products.unit_price / tr.rollerlength).toFixed(2) : '—' }}</span>
                        <span v-else-if="tr.length">{{ tr.operations_products ? (tr.operations_products.unit_price / (tr.length / 100)).toFixed(2) : '—' }}</span>
                        <span v-else>—</span>
                      </vs-td>
                      <vs-td>
                        <span v-if="tr.width && (tr.rollerlength || tr.length) && tr.operations_products">
                          {{ (tr.operations_products.unit_price / ((tr.width / 100) * (tr.rollerlength ? tr.rollerlength : tr.length / 100))).toFixed(2) }} €/m²
                        </span>
                        <span v-else>—</span>
                      </vs-td>
                      <vs-td>{{ tr.operations_products ? parseFloat(Math.abs(tr.operations_products.quantity) * tr.operations_products.unit_price).toFixed(4) : '—' }}</vs-td>
                      <vs-td v-if="hasTva(getOperationDetails(q.quotationref))">{{ tr.operations_products && tr.operations_products.tva_pct > 0 ? Math.abs(tr.operations_products.tva_pct).toFixed(2) : '—' }}</vs-td>
                      <vs-td v-if="hasTva(getOperationDetails(q.quotationref))">{{ tr.operations_products && tr.operations_products.tva_pct > 0 ? (Math.abs(tr.operations_products.quantity * tr.operations_products.unit_price) * (1 + (tr.operations_products.tva_pct || 0) / 100)).toFixed(4) : '—' }}</vs-td>
                      <vs-td>
                        <span :style="{ color: (tr.operations_products && tr.operations_products.isprior) ? 'red' : 'inherit' }">
                          {{ (tr.operations_products && tr.operations_products.isprior) ? 'Priority' : 'Normal' }}
                        </span>
                      </vs-td>
                    </vs-tr>
                  </template>
                </vs-table>
                <vs-alert v-if="sortedProductsForOperation(getOperationDetails(q.quotationref)).length > 0" color="success" title="Total" class="mt-3" active="true">
                  <span>Number of Units : {{ totalUnits(getOperationDetails(q.quotationref)) }} / Total Volume : {{ totalVolumeOp(getOperationDetails(q.quotationref)) }} m3</span>
                  <br />
                  Price per m3 (ex. VAT): {{ pricePerM3(getOperationDetails(q.quotationref)) }} EUR
                  <br /><br />
                  Total Potential Revenue ex VAT : {{ totalExVat(getOperationDetails(q.quotationref)) }} EUR
                  <br />
                  <span v-if="firstTvaPct(getOperationDetails(q.quotationref)) > 0">VAT : {{ firstTvaPct(getOperationDetails(q.quotationref)) }} %</span>
                  <br />
                  <span v-if="firstTvaPct(getOperationDetails(q.quotationref)) > 0">Total Potential Revenue inc VAT : {{ totalIncVat(getOperationDetails(q.quotationref)) }} EUR</span>
                </vs-alert>
              </template>
            </div>
          </div>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import axios from "@/axios.js";
import moduleDataListDashboard from "@/store/dashboard/moduleOperationList.js";

export default {
  name: "BrokerCheck",
  data() {
    return {
      loading: true,
      operationDetails: {},
    };
  },
  computed: {
    quotation_dashboard() {
      const d = this.$store.state.dataListDashboard;
      return (d && d.quotation_dashboard) ? d.quotation_dashboard : [];
    },
    quotation_dashboard_product() {
      const d = this.$store.state.dataListDashboard;
      return (d && d.quotation_dashboard_product) ? d.quotation_dashboard_product : [];
    },
    summaryByBroker() {
      const map = {};
      this.quotation_dashboard.forEach((q) => {
        const broker = q.broker || "—";
        if (!map[broker]) map[broker] = { broker, volume: 0, totalEur: 0 };
        map[broker].volume += Math.abs(parseFloat(q.volume || 0));
        const op = this.getOperationDetails(q.quotationref);
        map[broker].totalEur += parseFloat(this.totalExVat(op)) || 0;
      });
      return Object.values(map).sort((a, b) => (a.broker < b.broker ? -1 : 1));
    },
    summaryTotal() {
      let volume = 0;
      let totalEur = 0;
      this.summaryByBroker.forEach((row) => {
        volume += parseFloat(row.volume) || 0;
        totalEur += parseFloat(row.totalEur) || 0;
      });
      return { volume, totalEur };
    },
    summaryTableWithTotal() {
      const rows = this.summaryByBroker.map((r) => ({ ...r, isTotal: false }));
      rows.push({
        broker: "Total",
        volume: this.summaryTotal.volume,
        totalEur: this.summaryTotal.totalEur,
        isTotal: true,
      });
      return rows;
    },
    isSummaryValuesReady() {
      if (!this.quotation_dashboard.length) return true;
      return this.quotation_dashboard.every(
        (q) => this.operationDetails[q.quotationref] !== undefined
      );
    },
    quotation_dashboard_sorted() {
      const list = (this.quotation_dashboard || []).slice();
      return list.sort((a, b) => {
        const approvedA = a.date_approved ? new Date(a.date_approved).getTime() : 0;
        const approvedB = b.date_approved ? new Date(b.date_approved).getTime() : 0;
        if (approvedA !== approvedB) return approvedB - approvedA;

        const createdA = a.created ? new Date(a.created).getTime() : 0;
        const createdB = b.created ? new Date(b.created).getTime() : 0;
        return createdB - createdA;
      });
    },
  },
  methods: {
    formatSummaryValue(row) {
      const val = parseFloat(row.totalEur) || 0;
      if (!this.isSummaryValuesReady && val === 0) return "Calculating...";
      return val.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    displayDate(dateStr) {
      if (!dateStr) return "No date";
      const d = (dateStr || "").substring(0, 10);
      return d ? moment(d).format("DD-MM-YYYY") : "No date";
    },
    formatDeliveryAddress(q) {
      if (q.delivery_address_str) return q.delivery_address_str;
      const parts = [q.place, q.postal_code, q.country_code].filter(Boolean);
      return parts.length ? parts.join(", ") : "—";
    },
    formatVolumeReady(q) {
      const vol = parseFloat(q.volume || 0);
      const volToPrepare = parseFloat(q.volumetoprepare || 0);
      const ready = vol - volToPrepare;
      if (vol === 0) return "0.00 / 0.00 m3 (—)";
      const pct = (ready / vol) * 100;
      const pctStr = volToPrepare >= vol
        ? Math.abs(Math.round(pct)) + "% ready"
        : "-" + Math.abs(Math.round(pct)) + "% ready";
      return (
        Math.abs(ready).toFixed(2) +
        " / " +
        Math.abs(vol).toFixed(2) +
        " m3 (" +
        pctStr +
        ")"
      );
    },
    isQuotationFullyReady(q) {
      const vol = parseFloat(q.volume || 0);
      const volToPrepare = parseFloat(q.volumetoprepare || 0);
      if (vol === 0) return false;
      return ((vol - volToPrepare) / vol) * 100 == 100;
    },
    productsForQuotation(quotationref) {
      return this.quotation_dashboard_product.filter(
        (p) => String(p.quotationref) === String(quotationref)
      );
    },
    getOperationDetails(quotationref) {
      return this.operationDetails[quotationref] || null;
    },
    sortedProductsForOperation(op) {
      if (!op || !op.products || !op.products.length) return [];
      const list = op.products.slice();
      return list.sort((a, b) => {
        const catA = (a.productcategory && a.productcategory.name_en) || "";
        const catB = (b.productcategory && b.productcategory.name_en) || "";
        if (catA !== catB) return catA.localeCompare(catB);
        const descA = (a.operations_products && a.operations_products.description) || "";
        const descB = (b.operations_products && b.operations_products.description) || "";
        return descA.localeCompare(descB);
      });
    },
    needSumalForOperation(op) {
      if (!op || !op.products) return "—";
      for (let i = 0; i < op.products.length; i++) {
        const e = op.products[i];
        if (e.productcategory && e.productcategory.needsumal === true) return "Yes";
      }
      return "No";
    },
    hasTva(op) {
      const prods = op && op.products ? op.products : [];
      return prods.some(p => p.operations_products && p.operations_products.tva_pct > 0);
    },
    firstTvaPct(op) {
      const prods = op && op.products ? op.products : [];
      const first = prods.find(p => p.operations_products && p.operations_products.tva_pct > 0);
      return first && first.operations_products ? parseFloat(first.operations_products.tva_pct) : 0;
    },
    totalVolumeOp(op) {
      if (!op || !op.products) return "0.0000";
      let sum = 0;
      op.products.forEach(p => {
        if (p.volume && p.operations_products) sum += Math.abs(p.volume * p.operations_products.quantity);
      });
      return sum.toFixed(4);
    },
    totalUnits(op) {
      if (!op || !op.products) return "0";
      let sum = 0;
      op.products.forEach(p => {
        if (p.operations_products) sum += Math.abs(p.operations_products.quantity);
      });
      return sum.toFixed(2);
    },
    totalExVat(op) {
      if (!op || !op.products) return "0.00";
      let sum = 0;
      op.products.forEach(p => {
        if (p.operations_products) sum += Math.abs(p.operations_products.quantity) * parseFloat(p.operations_products.unit_price || 0);
      });
      return sum.toFixed(2);
    },
    pricePerM3(op) {
      const vol = parseFloat(this.totalVolumeOp(op)) || 0;
      const eur = parseFloat(this.totalExVat(op)) || 0;
      if (vol === 0) return "0.00";
      return (eur / vol).toFixed(2);
    },
    totalIncVat(op) {
      const exVat = parseFloat(this.totalExVat(op)) || 0;
      const pct = this.firstTvaPct(op) || 0;
      return (exVat + (exVat * pct) / 100).toFixed(2);
    },
    fetchAllOperationDetails() {
      const self = this;
      const ids = this.quotation_dashboard.map(q => q.quotationref);
      return Promise.all(ids.map(function(id) {
        return axios.get("/operation/data/" + id)
          .then(function(res) {
            self.$set(self.operationDetails, id, res.data);
          })
          .catch(function() {
            self.$set(self.operationDetails, id, null);
          });
      }));
    },
  },
  created() {
    const self = this;
    if (!moduleDataListDashboard.isRegistered) {
      this.$store.registerModule("dataListDashboard", moduleDataListDashboard);
      moduleDataListDashboard.isRegistered = true;
    }
    this.$store.dispatch("dataListDashboard/fetchQuotation")
      .then(function() {
        return self.$store.dispatch("dataListDashboard/fetchQuotationProduct");
      })
      .then(function() {
        self.loading = false;
        return self.fetchAllOperationDetails();
      })
      .catch(function(e) {
        console.error("BrokerCheck load error", e);
        self.$vs.notify({
          title: "Error",
          text: "Unable to load data.",
          color: "danger",
        });
        self.loading = false;
      });
  },
};
</script>

<style scoped>
.broker-check-page {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}
.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
}
.page-subtitle {
  color: #64748b;
  font-size: 0.9375rem;
}
.summary-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.summary-table .summary-total-row td {
  font-weight: 700;
  background: #e8f5e9;
  border-top: 1px solid #e2e8f0;
}
.detail-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.loading-wrap,
.empty-wrap {
  padding: 2.5rem;
  text-align: center;
  color: #64748b;
}
.detail-body {
  padding: 1rem 1.25rem 1.5rem;
}
.quotation-block {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}
.quotation-block:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
.quotation-header-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.quotation-chip {
  padding: 0.4rem 0.75rem;
  color: #fff !important;
}
.quotation-chip .quotation-ref,
.quotation-chip .quotation-sep,
.quotation-chip .quotation-date,
.quotation-chip .quotation-client,
.quotation-chip .quotation-vol {
  color: #fff !important;
}
.quotation-chip .quotation-header img {
  vertical-align: middle;
  flex-shrink: 0;
}
.quotation-flag-sep {
  flex-shrink: 0;
}
.quotation-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.5rem;
  font-size: 0.9375rem;
}
.broker-chip {
  padding: 0.5rem 1rem;
  font-weight: 700;
  font-size: 0.9375rem;
}
.broker-chip .broker-chip-icon {
  margin-right: 0.35rem;
  vertical-align: middle;
}
.broker-chip .broker-chip-label {
  color: #fff;
}
.broker-chip .broker-chip-approved {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  font-size: 0.875rem;
}
.broker-chip .broker-chip-created {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  font-size: 0.875rem;
}
.transport-chip {
  padding: 0.4rem 0.75rem;
}
.transport-chip .ml-1 {
  margin-left: 0.25rem;
  vertical-align: middle;
}
.addresses-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem 2rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}
.address-block {
  flex: 1;
  min-width: 200px;
}
.address-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0.2rem;
}
.address-value {
  font-size: 0.875rem;
  color: #334155;
}
/* Same style as dashboard analytics for quotation status chips */
#broker-check .ag-grid-cell-chips {
  line-height: 1;
}
.quotation-ref {
  font-weight: 600;
  color: #0f172a;
}
#broker-check .quotation-chip .quotation-ref-link {
  font-weight: 600;
  color: #fff !important;
  text-decoration: underline;
}
#broker-check .quotation-chip .quotation-ref-link:hover {
  color: rgba(255, 255, 255, 0.9) !important;
  text-decoration: underline;
}
.quotation-ref-link {
  font-weight: 600;
  color: #0f172a;
  text-decoration: none;
}
.quotation-ref-link:hover {
  text-decoration: underline;
  color: #1e40af;
}
.quotation-sep {
  color: #94a3b8;
}
.quotation-date {
  color: #475569;
}
.quotation-client {
  font-weight: 500;
}
.quotation-vol {
  font-weight: 600;
  color: #0f172a;
}
.products-table {
  margin-top: 0.5rem;
}
.internal-note-alert {
  border-radius: 6px;
}
.product-extra-info {
  font-size: 0.875rem;
}
.extra-info-table {
  width: auto;
  border-collapse: collapse;
}
.extra-info-table td {
  padding: 0.35rem 1rem 0.35rem 0;
  vertical-align: middle;
}
.extra-info-table td:first-child {
  color: #64748b;
  min-width: 140px;
}
</style>
